import classNames from "classnames";
import * as React from "react";
import { createPortal } from "react-dom";
import { useTransitionState } from "react-transition-state";

import { PORTAL_ROOT_ID } from "@src/PortalRoot";
import { useForm, useFormState } from "react-final-form";
import AsyncButton from "../../../derived/AsyncButton/AsyncButton";
import { ClickableDiv } from "../../../utils/ClickableDiv";
import Button from "../../buttons/Button/Button";
import Icon from "../../icons/Icon/Icon";
import Text from "../../type/Text/Text";
import { useFocusTrap } from "./useFocusTrap";

type TransitionStatus =
  | "preEnter"
  | "entering"
  | "entered"
  | "preExit"
  | "exiting"
  | "exited"
  | "unmounted";

const ESCAPE_KEY = "Escape";

type BaseModalProps = {
  readonly open?: boolean;
  readonly setOpen?: (isOpen: boolean) => void;
  readonly onClose?: () => void | Promise<void>;
  readonly onOpen?: () => void | Promise<void>;
  readonly children: React.ReactNode;
  readonly "data-testid"?: string;
  readonly dismissible?: boolean;
  readonly trapFocus?: boolean;
};

interface UnwrappedModalProps extends Omit<BaseModalProps, "onOpen"> {
  readonly state: TransitionStatus;
  readonly setOpen: (isOpen: boolean) => void;
  readonly labelledBy?: string;
}

const UnwrappedModal = React.forwardRef<HTMLDivElement, UnwrappedModalProps>(
  (props, ref) => {
    const {
      open,
      state,
      children,
      setOpen,
      "data-testid": dataTestid,
      onClose = () => {},
      dismissible = true,
      trapFocus = !dismissible,
      labelledBy,
    } = props;

    const [hasOpened, setHasOpened] = React.useState(false);
    const [contentEl, setContentEl] = React.useState<HTMLDivElement | null>(
      null
    );

    useFocusTrap(contentEl, trapFocus && state === "entered");

    React.useEffect(() => {
      if (!dismissible) return;

      const listener = (e: KeyboardEvent) => {
        if (e.key === ESCAPE_KEY) {
          setOpen(false);
          onClose();
        }
      };

      window.addEventListener("keyup", listener);

      return () => window.removeEventListener("keyup", listener);
    }, [onClose, setOpen, dismissible]);

    React.useEffect(() => {
      if (!hasOpened && state !== "exited") {
        setHasOpened(true);
      } else if (onClose && hasOpened && !open && state === "exited") {
        onClose();
        setHasOpened(false);
      }
    }, [onClose, hasOpened, open, state]);

    const root = document.getElementById(PORTAL_ROOT_ID);

    if (!root || state === "exited") return null;

    return createPortal(
      <div
        className={classNames(
          "fixed bottom-0 left-0 right-0 top-0 z-[1000] flex items-center justify-center overflow-auto px-[10vh] backdrop-blur-[1px] transition-opacity",
          {
            "pointer-events-auto opacity-100": state === "entered",
            "pointer-events-none opacity-0":
              state === "exiting" ||
              state === "entering" ||
              state === "preEnter" ||
              state === "preExit",
          }
        )}
        data-testid={dataTestid}
        ref={ref}
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
        <div
          className="fixed bottom-0 left-0 right-0 top-0 bg-straps-primary opacity-30"
          data-testid="modal-backdrop"
          onClick={(e) => {
            e.stopPropagation();
            if (dismissible && state === "entered") {
              setOpen(false);
              onClose();
            }
          }}
        />
        {/* This is only used to stop propagation of clicks on the modal content to prevent them from closing it */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
        <div
          className="relative z-[1001] mx-auto max-h-[80vh] max-w-[80vw] bg-white"
          onClick={(e) => e.stopPropagation()}
          ref={setContentEl}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          // Focus target when the dialog holds nothing focusable
          tabIndex={-1}
        >
          {children}
        </div>
      </div>,
      root
    );
  }
);

interface ModalProps extends BaseModalProps {
  // Element must have `onClick` prop
  readonly as?: React.ReactElement;
}

const Modal = ({ as, ...baseModalProps }: ModalProps) => {
  const [_open, _setOpen] = React.useState(false);
  const open =
    baseModalProps.open === undefined ? _open : Boolean(baseModalProps.open);
  const setOpen =
    baseModalProps.setOpen === undefined ? _setOpen : baseModalProps.setOpen;
  const titleId = React.useId();
  const [isTitled, setIsTitled] = React.useState(false);
  const contextValue = React.useMemo(
    () => ({
      setOpen,
      titleId,
      setIsTitled,
    }),
    [setOpen, titleId]
  );
  const ref = React.useRef<HTMLDivElement>(null);

  /** The fade duration is driven by the CSS transition-opacity class.
   * enter: 1 gives at least one frame in the "entering" state so the
   * opacity-0 → opacity-100 CSS transition can animate from.
   * exit: 250 keeps the element in the DOM long enough for the
   * fade-out to finish before the status moves to "exited". */
  const [transitionState, toggle] = useTransitionState({
    timeout: { enter: 1, exit: 250 },
    preEnter: true,
    initialEntered: open,
  });

  React.useEffect(() => {
    toggle(open);
  }, [open, toggle]);

  return (
    <>
      {as &&
        React.cloneElement(as, {
          onClick: (e: Event) => {
            setOpen(true);
            baseModalProps.onOpen && baseModalProps?.onOpen();
            e.stopPropagation();
          },
        })}
      <ModalContext.Provider value={contextValue}>
        <UnwrappedModal
          {...baseModalProps}
          state={transitionState.status}
          open={open}
          setOpen={setOpen}
          labelledBy={isTitled ? titleId : undefined}
          ref={ref}
        />
      </ModalContext.Provider>
    </>
  );
};

interface ModalContextState {
  setOpen: (isOpen: boolean) => void;
  titleId: string;
  setIsTitled: (isTitled: boolean) => void;
}
export const ModalContext = React.createContext<ModalContextState>({
  setOpen: () => {},
  titleId: "",
  setIsTitled: () => {},
});

const ModalCloseButton = ({
  onClick,
  as,
  ...props
}: React.ComponentPropsWithRef<typeof Button> & {
  readonly as?: React.ReactElement;
}) => {
  const { setOpen } = React.useContext(ModalContext);
  if (as) {
    return React.cloneElement(as, {
      onClick: () => {
        onClick?.();
        setOpen(false);
      },
    });
  }
  return (
    <Button
      dataTestId="modal-close-button"
      onClick={() => {
        onClick?.();
        setOpen(false);
      }}
      {...props}
    >
      {props.children === undefined ? "Cancel" : props.children}
    </Button>
  );
};

Modal.Close = ModalCloseButton;
Modal.Header = ModalHeader;
Modal.Footer = ModalFooter;
Modal.FormFooter = ModalFormFooter;

function ModalFormFooter(props: Omit<ModalFooterProps, "onSubmit">) {
  const form = useForm();
  const state = useFormState();

  const onSubmit = React.useCallback(async () => {
    await form.submit();
  }, [form]);

  return (
    <ModalFooter
      submitDisabled={!state.valid && state.submitFailed}
      onSubmit={onSubmit}
      {...props}
      closeAfterSubmit={state.valid ? props.closeAfterSubmit : false}
    />
  );
}

interface ModalHeaderProps {
  readonly iconProps?: React.ComponentPropsWithoutRef<typeof Icon>;
  readonly title?: string;
  readonly children?: React.ReactNode;
  readonly showCloseButton?: boolean;
  readonly onClose?: () => void;
  readonly variant?: "primary" | "plain";
  readonly autoHeight?: boolean;
}
function ModalHeader({
  iconProps,
  title,
  children,
  showCloseButton,
  onClose,
  variant = "primary",
  autoHeight = false,
}: ModalHeaderProps) {
  const { setOpen, titleId, setIsTitled } = React.useContext(ModalContext);

  React.useEffect(() => {
    if (!title) return;

    setIsTitled(true);
    return () => setIsTitled(false);
  }, [title, setIsTitled]);

  const isPlain = variant === "plain";
  const foreground = isPlain ? "primary" : "white";

  return (
    <div
      className={classNames("flex items-center justify-between px-7", {
        "bg-straps-primary": !isPlain,
        "h-15": !isPlain && !autoHeight,
        "border-b border-b-straps-accent-3 bg-white py-5": isPlain,
      })}
    >
      <div className="flex items-center gap-3">
        {iconProps && <Icon color={foreground} {...iconProps} />}
        {title && (
          <Text id={titleId} variant="h4_t-20-700" color={foreground}>
            {title}
          </Text>
        )}
        {children}
      </div>
      {showCloseButton && (
        <ClickableDiv
          onClick={onClose ? onClose : () => setOpen(false)}
          className="flex cursor-pointer items-center"
        >
          <Icon name="close" color={foreground} />
        </ClickableDiv>
      )}
    </div>
  );
}

interface ModalFooterProps {
  readonly children?: React.ReactNode;
  readonly cancelText?: string;
  readonly showCancel?: boolean;
  readonly onCancel?: () => void;
  readonly submitText?: string;
  readonly submitDisabled?: boolean;
  readonly showSubmit?: boolean;
  readonly onSubmit: (
    e: React.MouseEvent<HTMLElement>
  ) => Promise<unknown> | void;
  readonly closeAfterSubmit?: boolean;
  readonly submitButtonRef?: React.RefObject<HTMLButtonElement>;
  readonly submitDataTestId?: string;
}
function ModalFooter({
  children,
  cancelText = "Cancel",
  onCancel,
  onSubmit,
  submitDisabled,
  submitText = "Done",
  closeAfterSubmit,
  showCancel = true,
  showSubmit = true,
  submitButtonRef,
  submitDataTestId = "modal-save-button",
}: ModalFooterProps) {
  const { setOpen } = React.useContext(ModalContext);
  return (
    <div className="flex h-15 items-center border-t border-t-straps-accent-3 bg-white px-7">
      {children}
      <div className="ml-auto flex items-center">
        {showCancel && (
          <Text
            variant="sb_t-12-500"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                setOpen(false);
              }
            }}
            color="secondary"
            className="relative mx-4 cursor-pointer"
          >
            {cancelText}
          </Text>
        )}
        {showSubmit && (
          <AsyncButton
            onClick={async (e) => {
              await onSubmit(e);
              if (closeAfterSubmit) {
                setOpen(false);
              }
            }}
            ref={submitButtonRef}
            color="positive"
            disabled={submitDisabled}
            className="w-24 min-w-min whitespace-nowrap"
            data-cy={submitDataTestId}
            dataTestId={submitDataTestId}
          >
            {submitText}
          </AsyncButton>
        )}
      </div>
    </div>
  );
}

export default Modal;
